import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import userService from '../services/userService';

const usersCollectionName = 'users';

export const useUser = () => {
  return useQuery({
    queryKey: [usersCollectionName],
    queryFn: () => userService.getAll(),
    staleTime: 1000 * 60 * 60 * 24,
    cacheTime: 1000 * 60 * 60 * 24,
  });
};

export const useUserByRole = ({ user, enabled = true }) => {
  return useQuery({
    queryKey: ['usersByRole', user?.uid],
    queryFn: () => userService.fetchUsersByRole({ user }),
    enabled: enabled && !!user?.uid,
    staleTime: 1000 * 60 * 60 * 24,
    cacheTime: 1000 * 60 * 60 * 24,
  });
};

export const useAddUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userService.add,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [usersCollectionName] });
    },
  });
};

export const useEditUser = (userId) => {
  const queryClient = useQueryClient();
   return useMutation({
    mutationFn: userService.edit,
    onSuccess: () => {
      queryClient.invalidateQueries(["user", userId]);
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [usersCollectionName] });
    },
  });
};

export const useAddUserWithUUID = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userService.addWithUUID,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [usersCollectionName] });
    },
  });
};

export const useUserDivisions = (userId) => {
  return useQuery({
    queryKey: ['userDivisions', userId],
    queryFn: () => userService.getUserDivisions(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 60 * 24,
    cacheTime: 1000 * 60 * 60 * 24,
  });
};

export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userService.toggleUserStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [usersCollectionName] });
      queryClient.invalidateQueries({ queryKey: ['usersByRole'] });
    },
  });
};

export const useCheckDuplicates = () => {
  return useMutation({
    mutationFn: userService.checkDuplicates,
  });
};

export const useGenerateCustomToken = () => {
  return useMutation({
    mutationFn: (email) => {
      if (!email) {
        throw new Error('Email is required');
      }
      return userService.generateCustomToken(email);
    }
  });
};

export const useUserByEmail = (email, enabled = true) => {
  return useQuery({
    queryKey: ['userByEmail', email],
    queryFn: () => userService.getUserByEmail(email),
    enabled: enabled && !!email,
    staleTime: 1000 * 60 * 60 * 24,
    cacheTime: 1000 * 60 * 60 * 24,
  });
};

